/**
 * NativePreviewPane — one native widget, end to end.
 *
 *   left   the REAL runtime (`NativeWidgetRuntime`) in the first card of the
 *          strip — a 390×844 page at the strip's shared scale — with a page
 *          switcher, light/dark and
 *          mock/user data (`?data=mock|user`, deep-linkable beside `?widget=`)
 *          — the same interpreter the app runs — under the HOST's chrome: the
 *          '+' capture button and the within-widget back chevron, so the widget can
 *          actually be USED here and not just looked at. In My-data mode '+'
 *          opens the real capture sheet and runs the full lifecycle
 *          (photo → session → the agent logs back; see useHostCapture).
 *   right  ONE of four readings, chosen on the header's tab bar and carried in
 *          `?view=zone|module|trace|versions`:
 *            zone      the focus explorer's cards (`FocusExplorer`) — the
 *                      default, and the only one that is itself part of the strip.
 *            module    the settled force graph of the blueprint (`ModuleGraph`).
 *            trace     the host TRACE — what the blueprint's connections really
 *                      fired, plus any declared nav action.
 *            versions  the widget's REAL version ladder
 *                      (`widgetApi.listVersionLabels`), filtered to the NATIVE
 *                      (`view_type: 'ir'`) versions, with Publish-to-Public /
 *                      Delete-Public and the fail-closed report.
 *
 * THE LIVE PHONE IS NEVER UNMOUNTED. It is a running app with a capture sheet,
 * an interaction store and possibly an in-flight session (`useHostCapture`);
 * putting it inside the tab switch would throw all of that away on every hop.
 * Only the region to its right changes.
 *
 * A widget with no row in THIS environment is the normal case for a blueprint
 * authored in-repo: the versions pane says so plainly and the native preview
 * still renders, because the preview needs only the blueprint + a feed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VersionPane } from '../../widget/versionPane';
import { widgetApi, widgetInstances } from '../../../services/widgetApi';
import WidgetCaptureOverlay from '../../widget/WidgetCaptureOverlay';
import { pageHeaderStyle } from '../viewir/pageHeaderContext';
import NativeWidgetRuntime from '../NativeWidgetRuntime';
import { useInteractionStore } from '../viewir/slots';
import ModuleGraph from './ModuleGraph';
import FocusExplorer from './FocusExplorer';
import {
  pageOfNode, tapTarget, declaredOwner, ancestorsOf, pageFocusBinding,
} from './focusModel';
import PhoneCard from './PhoneCard';
import { ZONE_GAP, ZONE_LABEL_H, SCREEN_H } from './phoneMetrics';
import { createMockHost } from './mockHost';
import ItemOptionsSheet from './ItemOptionsSheet';
import { useHostCapture, openFilePicker } from './useHostCapture';
import { LocalMediaDraft } from '../viewir/localMediaDraft';
import { mockFeed, sampleAddition } from '../mock';
import { MOCK_STATES, injectStates, pageHasCollection } from './mockStates';

const C = {
  bg: '#f6f6f9', text: '#1c1c1e', textDim: '#6e6e73', accent: '#6E59F2',
  bgCard: '#fff', border: '#e2e2ea', good: '#248A3D', danger: '#b3261e',
};

/** A bridge id, as a non-empty string — anything else is no id at all. */
function runtimeId(value) {
  const s = value == null ? '' : String(value).trim();
  return s || null;
}

/**
 * EVERY HANDLE A FEED ROW ANSWERS TO — the row's own id AND the capture's, in
 * the same order the collections derive them (`ItemCollection.rowId`,
 * `SessionCollection.gridIdOf`, `PendingCard`). A delete is recorded by the
 * handle the CELL carried, so a row is only matched if one of its own handles
 * is that one.
 */
function rowHandles(row) {
  const r = row && typeof row === 'object' ? row : {};
  const p = r.payload && typeof r.payload === 'object' ? r.payload : {};
  return [
    r.item_id, r.id, r.entry_id, p.item_id,
    r.grid_id, r.session_id, r.source_session_id, r.photo_url, r.client_ref,
  ].map(runtimeId).filter(Boolean);
}

const btn = (on) => ({
  padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  border: `1px solid ${on ? C.accent : C.border}`,
  background: on ? C.accent : '#fff', color: on ? '#fff' : C.text,
});

/**
 * The detail view's layout, as a real stylesheet.
 *
 * Two rules earn their keep here and cannot be expressed inline:
 *   1. `min-height: auto` must SURVIVE. This pane is a flex item inside the
 *      studio's scrolling flex column; an inline `min-height: 0` (which this
 *      file used to carry) lets the column compress each section below its
 *      content, and the content then spills OVER the next section — the phone
 *      sitting on top of the graph's UI lane. `flex-shrink: 0` on every band
 *      is the fix, and it has to apply to children, so: a class.
 *   2. the top band is a two-track grid that collapses to one column on a
 *      narrow viewport — a media query, which inline styles cannot do.
 *
 * The pane is ONE horizontal strip: the live phone, then whatever the selected
 * tab puts to its right. In the `zone` reading the right side is more
 * `PhoneCard`s ([preview][data][focus][connection…]) — identical frames in one
 * flex row is what makes them align; the earlier versions kept differently
 * shaped zones side by side and spent their time chasing the offsets between
 * them. The other three readings are ONE wide panel that flexes to fill the
 * rest of the row and scrolls inside itself.
 */
const LAYOUT_CSS = `
.vi-native-detail { display: flex; flex-direction: column; gap: 20px; }
.vi-native-detail > * { flex: 0 0 auto; min-width: 0; }
`;

function DetailLayoutStyles() {
  return <style>{LAYOUT_CSS}</style>;
}

/**
 * A widget's PRIMARY add affordance, read off the blueprint: the host slot its
 * capture button fires, preferring a capture button source over a Button over an
 * empty-state CTA. medicine-box declares none — the HOST owns the '+' either
 * way (registry `CaptureButton`), so `openCapture` is the floor rather than "this widget
 * cannot add".
 */
const ADD_SLOTS = ['openCapture', 'openMainSession', 'logData'];

/** Host slots the studio dispatches for REAL (open the sheet / open the chat). */
const SESSION_TRIGGER = new Set(['openCapture', 'openMainSession', 'openSession']);

/**
 * The prompt a session payload carries, in the two shapes the fleet writes it:
 * TOP-LEVEL (`captureFab → openCapture {prompt}`, `errorPane → openMainSession
 * {prompt}`) or NESTED under `args` (a delegated template preflight, whose
 * `args` holds the template's verbatim locked prompt). Null when neither.
 */
function sessionPrompt(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  if (typeof p.prompt === 'string' && p.prompt) return p.prompt;
  const nested = p.args && typeof p.args === 'object' ? p.args.prompt : null;
  return typeof nested === 'string' && nested ? nested : null;
}

/** The pick menu's footprint, and the margin it keeps off the viewport edge. */
const MENU_W = 216;
const MENU_H = 118;
const MENU_HINT_H = 40;
const MENU_EDGE = 8;

/**
 * THE POPUP IS THE STUDIO'S CAMERA — for every widget, not just the one that
 * needed it first.
 *
 * There are exactly two moments a native widget asks the host for photographs:
 * `pick_media` (a role form filling its own wells) and a DEFERRED capture
 * (`open_capture {defer_dispatch, target_page}` — the host takes the pages and
 * hands them to an edit page before anything is dispatched). The phone answers
 * both with its picker; the studio answers both with this one pill, so a
 * developer learns one affordance and every widget that grows a deferred
 * capture inherits it with no studio change at all. Only the heading and the
 * one-line reason differ, and both come from the caller.
 */
const PICK_MEDIA_ASK = {
  label: 'host.pickMedia',
  hint: null,
};
const DEFERRED_ASK = {
  label: 'host.openCapture · defer_dispatch',
  hint: 'The pages this capture takes — the studio’s stand-in for the phone camera. '
    + 'They open the edit page; nothing is dispatched until you confirm there.',
};

/**
 * How many pages one deferred capture may hand over when the payload does not
 * say. A scan is a MULTI-page thing on the phone (the camera keeps shooting
 * until the user stops), so the studio's stand-in must let a developer hand
 * over more than one and see the editor's pager.
 */
const DEFERRED_PAGES = 5;

/**
 * The pick menu's TOP-LEFT — the point that was tapped, clamped on screen.
 *
 * The pill floats at the cursor because that is where the well is; a tap near
 * the right or bottom edge would put it half off-screen, so each axis is pinned
 * inside the viewport. No pointer yet (a keyboard-driven pick, jsdom) → the
 * top-left corner, which is at least always visible.
 */
function menuAnchor(point, height = MENU_H) {
  const vw = globalThis.innerWidth || 1024;
  const vh = globalThis.innerHeight || 768;
  const clamp = (v, size, extent) => Math.min(
    Math.max(MENU_EDGE, typeof v === 'number' ? v : MENU_EDGE),
    Math.max(MENU_EDGE, extent - size - MENU_EDGE),
  );
  return { x: clamp(point?.x, MENU_W, vw), y: clamp(point?.y, height, vh) };
}

/**
 * The value a `to: "page_args.<key>"` connection writes — the interpreter's own
 * rule (interpreter.jsx, the `page_args` branch), read a second time so the
 * studio can MIRROR the write without the runtime having to report it: the key's
 * own field if the payload carries one, else `value`, else `key`. Both readings
 * are of the same delivered payload, so they cannot disagree about a wire; if
 * the interpreter's rule ever grows a case, this is its twin.
 */
function pageArgValue(payload, key) {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload[key] !== undefined) return payload[key];
  if (payload.value !== undefined) return payload.value;
  return payload.key;
}

/**
 * A host ANSWER the diagram cards cannot correlate for themselves.
 *
 * `pickMedia` resolves with photos and nothing else; WHICH well asked is held
 * in the live form's own ref (`RoleMediaForm`'s self-correlation), so a second
 * copy of that form — the focus card — receives the same `setValue` delivery
 * with no role to put it in and drops it. The request rides back on the signal
 * (`host.hostResult`'s `request`), so the studio can re-address the delivery
 * explicitly: an explicit `role_id` on the payload always wins, which is the
 * documented door a host uses to fill a named well.
 *
 * Returns the re-addressed delivery, or null when the trace is anything else.
 */
function correlatedDelivery(evt) {
  const to = String(evt?.to || '');
  const dot = to.indexOf('.');
  const slot = dot < 0 ? '' : to.slice(dot + 1);
  const payload = evt?.payload;
  if (slot !== 'setValue' || !payload || typeof payload !== 'object') return null;
  const roleId = payload.role_id ?? payload.request?.role_id ?? null;
  if (!roleId) return null;
  return { nodeId: to.slice(0, dot), slot, payload: { ...payload, role_id: roleId } };
}

/** A field off the payload or its nested `args`, whichever carries it. */
function sessionField(payload, key) {
  const p = payload && typeof payload === 'object' ? payload : {};
  if (p[key] !== undefined && p[key] !== null) return p[key];
  const nested = p.args && typeof p.args === 'object' ? p.args[key] : undefined;
  return nested === undefined ? null : nested;
}

function primaryAdd(blueprint) {
  const comps = blueprint?.components || {};
  const rank = (from) => ({ CaptureButton: 0, Button: 1 }[comps[String(from).split('.')[0]]?.type] ?? 2);
  const hits = (blueprint?.connections || [])
    .filter((c) => String(c.to || '').startsWith('host.') && ADD_SLOTS.includes(String(c.to).slice(5)))
    .map((c) => ({ from: c.from, slot: String(c.to).slice(5) }))
    .sort((a, b) => rank(a.from) - rank(b.from) || ADD_SLOTS.indexOf(a.slot) - ADD_SLOTS.indexOf(b.slot));
  // A CaptureButton is the explicit '+'. With none (e.g. medicine-box's add door
  // is the host FAB), openCapture is the floor — so a detail-page Button wired to
  // openMainSession must NOT be mistaken for the home '+'. Prefer an openCapture
  // hit before falling to the top-ranked one, then to the floor.
  const capture = hits.find((h) => h.slot === 'openCapture');
  if (hits[0] && rank(hits[0].from) === 0) return hits[0];
  return capture || hits[0] || { from: null, slot: 'openCapture' };
}

/**
 * MARKER-ONLY INSPECT for the live preview.
 *
 * The preview is the app, not a diagram: nothing is dimmed (`dim` is empty and
 * stays empty) and nothing is boxed (`box` is absent), so the render is pixel
 * for pixel what it was — `inspectWrap` adds only a `display: contents` wrapper,
 * which generates no box of its own. What it DOES add is the `[data-vi-node]`
 * id on every node, which is the only thing in the DOM that can answer "which
 * module did that tap land on". Without it a tap in the preview could not move
 * the studio's selection at all.
 *
 * Module-level so its identity is stable — a fresh object each render would
 * re-render the whole page tree on every keystroke elsewhere in the pane.
 */
const PREVIEW_INSPECT = { dim: new Set() };

function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', gap: 4 }}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)} style={btn(o.value === value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The four readings of a widget, in the order the header shows them, and the
 * values `?view=` takes. Deep-linkable on purpose: a particular reading of a
 * particular widget is the thing people paste to each other, and `studioNav`
 * parks the whole search string across a tab hop.
 */
const VIEWS = [
  { id: 'zone', label: 'Zone' },
  { id: 'module', label: 'Module graph' },
  { id: 'trace', label: 'Host Trace' },
  { id: 'versions', label: 'Versions' },
];
const DEFAULT_VIEW = 'zone';

/**
 * The header's tab bar. A sibling of `Segmented` rather than a prop on it,
 * because one tab can carry an ATTENTION dot (the public pin is not the latest
 * native version) and that dot is `aria-hidden` — the tab's accessible name has
 * to stay exactly its label so it is still `getByRole('tab-name')`-able.
 */
function ViewTabs({ value, onChange, attention }) {
  return (
    <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }} data-testid="native-view-tabs">
      {VIEWS.map((v) => (
        <button
          key={v.id}
          type="button"
          data-testid={`native-tab-${v.id}`}
          data-active={v.id === value ? '1' : undefined}
          onClick={() => onChange(v.id)}
          style={{ ...btn(v.id === value), position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          {v.label}
          {v.id === 'versions' && attention ? (
            <span
              aria-hidden="true"
              data-testid="versions-attention-dot"
              title="The public pin is not the latest native version"
              style={{
                width: 7, height: 7, borderRadius: 4, flex: '0 0 7px',
                background: v.id === value ? '#fff' : C.danger,
              }}
            />
          ) : null}
        </button>
      ))}
    </div>
  );
}

export default function NativePreviewPane({ blueprint, registry, onBack, showFab = true }) {
  const widgetId = blueprint?.widget_id;
  const pages = useMemo(() => blueprint?.pages || [], [blueprint]);
  const [pageId, setPageId] = useState(() => (pages.find((p) => p.entry) || pages[0])?.id || null);
  const [appearance, setAppearance] = useState('light');
  // The widget's built-in sample data, as a selectable option (default on).
  // Unselect it to preview the widget's EMPTY state (no seeded rows). Not
  // page-scoped — it's a global preview preference that persists across pages.
  const [showMock, setShowMock] = useState(true);
  // Which lifecycle states the admin has injected into the current page's mock
  // render (see mockStates). Empty = the base mock. Page-scoped: cleared on a
  // page change because the applicable states differ per page.
  const [mockStates, setMockStates] = useState(() => new Set());
  const toggleMockState = useCallback((key) => setMockStates((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);
  // The args the HOST opened the current page on — the navigation's own answer,
  // fixed for as long as the page is up.
  const [pageArgs, setPageArgs] = useState({});
  /**
   * …and what the PREVIEW has written to `page_args` since it landed there.
   *
   * The diagram cards are top-down readings of the running app, and a page's
   * live state is not only what it was opened with: scene-studio's role form
   * parks `role_urls` / `form_complete` / `submit_label` in `page_args` on every
   * pick, and the submit's own label is a pure binding on that. Each card runs
   * its OWN interpreter, so without this the preview says "Create scene" while
   * the focus card two frames to the right still says "Add photo above" — the
   * same page, two answers. Tagged with the page it belongs to so a navigation
   * drops it without a reset anywhere.
   */
  const [liveArgs, setLiveArgs] = useState({ page: null, args: null });
  /**
   * The last host pick answer, RE-ADDRESSED to the well that asked for it (see
   * `correlatedDelivery`) — the other half of the same mirror, and the only one
   * the label cannot carry: a well's photo lives in the live form's own state,
   * not in `page_args`.
   */
  const [pickMirror, setPickMirror] = useState(null);
  const [trace, setTrace] = useState([]);
  const [issues, setIssues] = useState([]);
  const [dataState, setDataState] = useState('ready');
  // What the page asked the host's NAV BAR to offer (`host.declareNavAction`).
  // The app draws it in its own chrome; the studio draws it beside the trace,
  // because a declared nav action has no in-page control by design — without
  // this button the half of the blueprint that answers it is unreachable.
  const [navAction, setNavAction] = useState(null);

  /**
   * THE HOST'S LONG-PRESS SHEET — what `host.itemOptions` opens.
   *
   * A long press (right-click, or a 500ms hold — the same gesture the phone
   * calls a long press) emits `removeRequested`; the blueprint wires it to
   * `host.itemOptions`; the HOST answers by drawing the sheet, whose Delete
   * tile performs the removal. Never a delete on the gesture alone, on either
   * kit. `itemMenu` is the payload the slot carried (`{item_id}` for a logged
   * row, `{grid_id, session_id}` for an in-flight capture — both must delete).
   */
  const [itemMenu, setItemMenu] = useState(null);
  const [itemMenuBusy, setItemMenuBusy] = useState(false);
  /**
   * WHAT THE PREVIEW HAS DELETED. The studio has no store of its own — the
   * feed is the widget's sample mock (or the user's real rows) — so a mock-mode
   * delete is recorded here and filtered out of the feed the same way the
   * append seam mutates it: the preview is a preview, and the load-bearing
   * delete is the phone's. In "My data" mode the sheet calls the REAL
   * `DELETE /data/{entry_id}` and this stays empty.
   */
  const [removedRows, setRemovedRows] = useState(() => new Set());
  // A real delete has to re-read the feed; `useHostCapture` owns the capture's
  // own reload counter, so this is the sheet's half of the same key.
  const [deleteReload, setDeleteReload] = useState(0);

  // `?data=mock|user` rides in the URL beside `?widget=`, so "My data" is
  // deep-linkable and the back button returns to the mode you were in.
  const [params, setParams] = useSearchParams();
  // A confirmed Session handoff is terminal for this Studio visit. Queued
  // form traces must not replace the chat route with an old focus query.
  const leavingForSession = useRef(false);
  const dataMode = params.get('data') === 'user' ? 'user' : 'mock';
  const setDataMode = (mode) => { params.set('data', mode); setParams(params); };

  // Another widget, or another feed, is another set of rows: what THIS preview
  // deleted says nothing about them, and an open sheet belongs to a cell that
  // is no longer on screen.
  useEffect(() => { setRemovedRows(new Set()); setItemMenu(null); }, [widgetId, dataMode]);

  // …and so does the selected READING (`?view=`), so a particular tab of a
  // particular widget can be linked to and screenshotted.
  const asked = params.get('view');
  const view = VIEWS.some((v) => v.id === asked) ? asked : DEFAULT_VIEW;
  const setView = (id) => { params.set('view', id); setParams(params); };
  // The focus explorer's selected module, likewise. REPLACE, not push: tapping
  // your way around a blueprint must not bury the back button in history.
  const focusNode = params.get('focus');
  // The focus card's PAGE is tracked separately from the focused module: a
  // release clears the highlight and keeps the page you were reading.
  const focusPage = params.get('page') || (blueprint?.pages?.find((p) => p.entry) || blueprint?.pages?.[0])?.id || null;
  const setFocusNode = (id) => {
    if (leavingForSession.current) return;
    if (id) {
      params.set('focus', id);
      // FOLLOW the node onto its page only when it really is on one.
      // `pageOfNode` fails soft — an id whose root owns no page falls back to
      // the ENTRY page, which is the right answer for "where do I start" and
      // very much the wrong one here: writing it turned a selection into a
      // NAVIGATION, and the focus zone (which is a diagram, not the app) took
      // the whole strip back to index. Selecting something can move the page;
      // failing to place it must not.
      const p = pageOfNode(blueprint, id);
      if (p?.id && p.root === ancestorsOf(blueprint, id).at(-1)) params.set('page', p.id);
    } else {
      params.delete('focus'); // release the module, STAY on the page
    }
    setParams(params, { replace: true });
  };
  /**
   * A page change with no module in mind (the host navigated, the toggle, the
   * back chevron). The old focus belonged to the page you just left, so it is
   * released — the page, which is now its own state, stays.
   */
  const setFocusPage = (id) => {
    if (leavingForSession.current) return;
    if (id) params.set('page', id); else params.delete('page');
    params.delete('focus');
    setParams(params, { replace: true });
  };
  // Refs are synced in an effect, never during render.
  useEffect(() => {
    setFocusRef.current = setFocusNode;
    focusPageRoot.current = setFocusPage;
  });

  // ── the REAL version ladder ───────────────────────────────────────────
  const [versionList, setVersionList] = useState(null);
  const [versionsError, setVersionsError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadVersions = useCallback(async () => {
    if (!widgetId) return;
    setVersionsError(null);
    try {
      const r = await widgetApi.listVersionLabels(widgetId);
      setVersionList(r);
      const all = r?.versions || [];
      const pub = all.find((v) => v.public);
      // The ladder is newest-first, so the first `ir` row IS the latest native
      // one. Default the selection to a NATIVE label: this studio's panel lists
      // only native versions, and defaulting to a webview label would leave no
      // row looking selected while Publish silently pinned the webview bundle.
      const native = all.find((v) => v.view_type === 'ir');
      const preferred = pub?.view_type === 'ir' ? pub : (native || pub || all[0]);
      setSelectedVersion((prev) => prev || preferred?.version_label || null);
    } catch (e) {
      // A blueprint authored in-repo does NOT imply a widget row here. Say so
      // rather than showing "no versions", which reads as data loss.
      setVersionList({ versions: [] });
      setVersionsError(e.message || String(e));
    }
  }, [widgetId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  /**
   * THE LADDER, READ TWICE.
   *
   * `allVersions` is the ladder as the server merged it — native AND webview
   * rows for the same widget, because a widget that grew a native blueprint
   * usually still has its HTML bundle history behind it. The PIN check has to
   * run over that full list (its whole point is to notice that PUBLIC is a
   * webview version, i.e. native is not being served at all).
   *
   * `nativeVersions` is what this studio DISPLAYS: a native studio listing a
   * webview label you cannot preview here is an invitation to pin the wrong
   * thing. The filter lives here and not in `VersionPane`, which is shared with
   * the HTML studio and must keep showing that studio everything.
   */
  const allVersions = useMemo(() => versionList?.versions || [], [versionList]);
  const nativeVersions = useMemo(() => allVersions.filter((v) => v.view_type === 'ir'), [allVersions]);
  const nativeList = useMemo(
    () => (versionList ? { ...versionList, versions: nativeVersions } : null),
    [versionList, nativeVersions],
  );
  const publicEntry = allVersions.find((v) => v.public) || null;
  const latestNative = nativeVersions[0] || null;
  /**
   * ATTENTION: what the app is serving is not the newest native version — the
   * pin is an older native one, or a WEBVIEW one, or there is no pin at all.
   * An unknown ladder (fetch failed, no widget row in this env) is NOT stale:
   * `latestNative` is null there, so nothing lights up.
   */
  const versionAttention = Boolean(latestNative && publicEntry?.version_label !== latestNative.version_label);
  const tags = publicEntry ? { release: { version_label: publicEntry.version_label } } : {};
  const notInEnv = Boolean(versionsError);

  const publish = async () => {
    if (!selectedVersion) return;
    setBusy('publish');
    try {
      await widgetApi.publish(widgetId, selectedVersion);
      await loadVersions();
    } catch (e) {
      setVersionsError(e.message || String(e));
    } finally { setBusy(null); }
  };

  const unpublish = async () => {
    setBusy('clear');
    try {
      await widgetApi.clearTag(widgetId, 'release');
      await loadVersions();
    } catch (e) {
      setVersionsError(e.message || String(e));
    } finally { setBusy(null); }
  };

  // The mock host and the trace sink are built once; these give them the
  // current focus setter without rebuilding either on every render.
  const setFocusRef = useRef(null);
  const focusPageRoot = useRef(null);
  // The real session opener (defined below, after `capture`). Held in a ref so
  // the mock host — built above it — can call the latest one without rebuilding.
  const onSessionOpenRef = useRef(null);
  // …and the photo picker's answer, for the same reason: the pane asks the
  // developer which photo (see the pick menu) and the promise it returns is
  // what the bridge awaits.
  const pickSampleRef = useRef(null);

  // ── in-widget navigation ──────────────────────────────────────────────
  // The host owns every page transition, so it also owns the way BACK: each
  // `openItem`/`openPage` pushes the page it left onto a stack and the host's
  // back chevron pops it. Landing on the ENTRY page is a pop-to-root — the
  // blueprint's `closePage` goes there, and it is the bottom of the stack.
  const entryId = useMemo(() => (pages.find((p) => p.entry) || pages[0])?.id || null, [pages]);
  const [backStack, setBackStack] = useState([]);
  const here = useRef({ pageId, pageArgs });
  here.current = { pageId, pageArgs };
  // An opt-in capture belongs to this visit, not only to a reusable page ID.
  const captureOrigin = useMemo(() => ({ active: false }), [widgetId, pageId, pageArgs]);
  useEffect(() => {
    captureOrigin.active = true;
    return () => { captureOrigin.active = false; };
  }, [captureOrigin]);

  const goBack = useCallback(() => {
    const prev = backStack[backStack.length - 1];
    setBackStack((s) => s.slice(0, -1));
    const landed = prev?.pageId ?? entryId;
    setPageId(landed);
    setPageArgs(prev?.pageArgs || {});
    setFocusPage(landed);
  }, [backStack, entryId]);

  /**
   * WHERE `openItem` LANDS — declared by the blueprint, never named here.
   *
   * The detail page is whichever non-entry page is bound to `focusEntry`
   * (`pageFocusBinding`: its root declares the bind, or its modules read
   * `focusEntry.*`). The mock bridge used to navigate to the literal string
   * `data_view` — a WebView page id — so renaming that page left every tap on
   * a card opening nothing, with no error to read.
   */
  const itemPage = useMemo(
    () => pages.find((p) => !p.entry && pageFocusBinding(blueprint, p) === 'focusEntry')?.id || null,
    [blueprint, pages],
  );

  /**
   * A TARGET PAGE, in every spelling the fleet writes one.
   *
   * `openPage` says `page_file`/`file`/`page` and a deferred capture says
   * `target_page`, and both are written either as the native page id (`edit`)
   * or as its bundle routing key (`edit.html`) — the app routes by FILENAME, so
   * a blueprint that wants to be reachable on the phone must spell the `.html`.
   * Resolving all of them here is what keeps the studio from silently
   * navigating nowhere, which is the one failure mode a page transition has.
   */
  const resolvePage = useCallback((target) => {
    const key = target == null ? '' : String(target).trim();
    if (!key) return null;
    return pages.find((p) => p.id === key || p.page_file === key || p.page_file === `${key}.html`) || null;
  }, [pages]);

  /**
   * THE HOST OWNS EVERY PAGE TRANSITION — one door, whoever knocks.
   *
   * The bridge knocks for `openItem`/`openPage`; the DEFERRED capture handoff
   * below knocks directly, the way the phone's `_openDeferredCapture` pushes the
   * edit route itself instead of going back out through the bundle.
   */
  const navigatePage = useCallback((page, args, { backToRoot = false } = {}) => {
    const known = resolvePage(page);
    if (!known) return;
    if (known.id === here.current.pageId) {
      // 同一目标页可能先被宿主打开、再由 Session 回执补齐定位参数；后者不能被当成重复导航丢掉。
      if (args && Object.keys(args).length) setPageArgs(args);
      if (backToRoot) setBackStack(entryId ? [{ pageId: entryId, pageArgs: {} }] : []);
      return;
    }
    setBackStack((s) => {
      if (known.entry) return [];
      // 创建结果是上传流程的最终落点，返回时应直接回到 Widget 首页。
      if (backToRoot) return entryId ? [{ pageId: entryId, pageArgs: {} }] : [];
      return [...s, here.current];
    });
    setPageId(known.id);
    setPageArgs(args || {});
    // The focus zone MIRRORS the preview: a page jump moves the focus card
    // to that page. The page is its own state, so it survives a release.
    focusPageRoot.current?.(known.id);
  }, [entryId, resolvePage, setBackStack]);

  // ── the host bridge (mock) ────────────────────────────────────────────
  // 暂存随页面销毁；StrictMode 的 effect 重挂仅恢复生命周期，不跨页面复用照片。
  const localMediaDraft = useMemo(() => new LocalMediaDraft(), [widgetId, pageId]);
  useEffect(() => {
    localMediaDraft.activate();
    return () => localMediaDraft.dispose();
  }, [localMediaDraft]);
  const host = useMemo(() => createMockHost({
    localMediaDraft,
    push: (evt) => setTrace((prev) => [evt, ...prev].slice(0, 40)),
    onNavAction: setNavAction,
    itemPage,
    // A session slot fired in the LIVE preview really starts it — the same
    // `_openCapture` / open-session the app runs. Via a ref so the host is not
    // rebuilt when the opener's closure changes.
    onSessionOpen: (slot, payload) => onSessionOpenRef.current?.(slot, payload),
    // `pick_media` ANSWERS — with a genuinely uploaded photo, so a widget that
    // collects its own materials (the `template_upload` role form) can actually
    // be completed here. Without an answer the wells never fill and the lane
    // dead-ends one step before the session it exists to start.
    pickSample: (max, payload) => pickSampleRef.current?.(max, payload),
    // A page THIS bundle renders natively vs a delegated WebView file.
    isNativePage: (id) => Boolean(resolvePage(id)),
    // LONG PRESS → THE HOST'S SHEET, never a delete. `crudRemove` is aliased to
    // the same opener inside the mock (it is the primitive the sheet's Delete
    // calls), so a blueprint whose wire has not been retargeted yet still
    // behaves here the way it behaves on the phone.
    onItemOptions: (payload) => setItemMenu(payload || {}),
    navigatePage,
  }), [navigatePage, resolvePage, itemPage, localMediaDraft]);

  const onReport = useCallback((list) => setIssues(list || []), []);

  // The node the last preview TAP resolved to, and when. See `onTrace`.
  const lastTap = useRef({ id: null, at: 0 });

  /**
   * Every connection the preview fires arrives here as `from: "<node>.<signal>"`
   * — which is exactly "the module the user just touched". Focusing it keeps
   * the focus zone in step with the live app. `host.*` sources are the runtime
   * talking to itself (`dataReceived` on every feed push), never a tap.
   *
   * The TAP is the more precise reading of the same gesture, though: a card
   * re-emits through its grid, so a tap on one medicine traces `shelves` while
   * the tap itself resolved `shelves__card__<id>` — the row. When both describe
   * the same gesture (the traced module IS the tapped node's declared owner,
   * within the same tick) the tap wins, so the selection keeps the row identity
   * the data zone needs. Anything else the connection fires still focuses it.
   */
  const onTrace = useCallback((evt) => {
    // ── the LIVE MIRROR, before anything about the focus ────────────────
    // Both zones read the same running page, so every fact the preview writes
    // about it is recorded here and handed to the diagram cards. See `liveArgs`.
    const to = String(evt?.to || '');
    if (to.startsWith('page_args.')) {
      const key = to.slice('page_args.'.length);
      const value = pageArgValue(evt.payload, key);
      const page = here.current.pageId;
      setLiveArgs((prev) => ({
        page,
        args: { ...(prev.page === page ? prev.args : null), [key]: value },
      }));
    }
    const answer = correlatedDelivery(evt);
    if (answer) setPickMirror((prev) => ({ ...answer, page: here.current.pageId, seq: (prev?.seq || 0) + 1 }));

    const from = String(evt?.from || '');
    const id = from.slice(0, from.indexOf('.'));
    if (!id || id === 'host') return;
    const tap = lastTap.current;
    if (tap.id && Date.now() - tap.at < 400 && declaredOwner(blueprint, tap.id) === id) return;
    setFocusRef.current?.(id);
  }, [blueprint]);

  /**
   * THE PREVIEW REPORTS ITS TAPS — and never blocks one.
   *
   * The focus zone selects by SWALLOWING taps (`InspectLayer`); the preview
   * cannot, because it is the running app and its taps have to reach it. So this
   * is a capture-phase listener on the phone's frame that only READS the gesture:
   * `elementsFromPoint` → the nearest `[data-vi-node]` marker → `tapTarget`, the
   * same resolution the inspect overlay does, with no `preventDefault` and no
   * `stopPropagation` anywhere in it. The '+', the back chevron and every card
   * keep behaving exactly as they did.
   *
   * Setting the focus is all it takes to move BOTH other zones: the focus card
   * boxes and scrolls to the node, and the data zone auto-picks its row and
   * scrolls to that. Host chrome resolves to no marker — a tap on '+' must not
   * be read as "release the selection", so a miss changes nothing.
   */
  const previewFrame = useRef(null);
  useEffect(() => {
    const frame = previewFrame.current;
    if (!frame) return undefined;
    const report = (e) => {
      const stack = globalThis.document?.elementsFromPoint?.(e.clientX, e.clientY) || [];
      const ids = [];
      for (const el of stack) {
        if (!frame.contains?.(el)) continue;
        const id = el.closest?.('[data-vi-node]')?.getAttribute('data-vi-node');
        if (id && !ids.includes(id)) ids.push(id);
      }
      const node = tapTarget(blueprint, ids);
      if (!node) return;
      lastTap.current = { id: node, at: Date.now() };
      setFocusRef.current?.(node);
    };
    frame.addEventListener('click', report, true);
    return () => frame.removeEventListener('click', report, true);
  }, [blueprint]);

  /**
   * WHERE THE LAST TAP LANDED — the pick menu's anchor, and nothing else.
   *
   * The menu opens in answer to `host.pickMedia`, which reaches the pane through
   * the interpreter, one async hop after the well was tapped: by then the event
   * is long gone and only the widget knows which well it was. Reading the
   * pointer here — a capture-phase listener that touches nothing — is what lets
   * the menu appear AT the well the developer tapped instead of in a corner of
   * the studio. The alternative is threading coordinates through the kit's
   * `pickRequested`, which would put a studio-only concern in the component the
   * phone renders.
   */
  const pointer = useRef({ x: null, y: null });
  useEffect(() => {
    const mark = (e) => {
      if (typeof e?.clientX !== 'number') return;
      pointer.current = { x: e.clientX, y: e.clientY };
    };
    globalThis.addEventListener?.('pointerdown', mark, true);
    globalThis.addEventListener?.('mousedown', mark, true);
    return () => {
      globalThis.removeEventListener?.('pointerdown', mark, true);
      globalThis.removeEventListener?.('mousedown', mark, true);
    };
  }, []);

  // ── the host capture button (host chrome, NOT the blueprint) ─────────────────────
  const primary = useMemo(() => primaryAdd(blueprint), [blueprint]);
  const [addError, setAddError] = useState(null);
  // `pageId`: the sheet belongs to the page that asked for it — see the hook.
  const capture = useHostCapture({ widgetId, widgetTitle: blueprint?.title, dataMode, pageId });
  const navigate = useNavigate();
  const sessionCaptureBusy = useRef(null);

  /**
   * THE PHOTOS — ASKED FOR, never assumed. (`host.pickMedia`, and the camera
   * half of a deferred capture.)
   *
   * The phone answers with one photo picker because the phone has one answer.
   * The studio has two worth testing — the widget's own representative sample
   * (walk the lane in two clicks) and a real file off this machine (see the lane
   * with a photo it was not built around) — and picking for the developer is how
   * the old studio ended up filling wells with a stock image nobody chose. So it
   * ASKS, in a pill at the point that was tapped, and the promise the caller is
   * waiting on resolves on the click.
   *
   * `ask` holds the pending question's `resolve`. For `pick_media` the answer
   * travels back through the same `pickMedia → hostResult → setValue` chain the
   * app uses, so the well correlation is untouched — only the source of the
   * photo changes; for a deferred capture the answer IS the captured pages.
   * Dismissing answers `[]`, exactly like a cancelled picker, and every caller
   * has to survive that: it is the phone user backing out of the camera.
   */
  const [pickAsk, setPickAsk] = useState(null); // { max, label, hint, x, y }
  const ask = useRef(null);

  const answerPick = useCallback((urls) => {
    const pending = ask.current;
    ask.current = null;
    setPickAsk(null);
    pending?.resolve(Array.isArray(urls) ? urls : []);
  }, []);

  const askForMedia = useCallback((max, why = PICK_MEDIA_ASK, payload = {}) => new Promise((resolve) => {
    // One question at a time: a still-open menu belongs to a pick nobody
    // answered, and leaving its promise unresolved would hang that well.
    ask.current?.resolve([]);
    ask.current = { resolve, payload };
    setPickAsk({
      deferred: payload.uploadTiming === 'on_submit',
      max: Math.max(1, Math.trunc(Number(max) || 1)),
      label: why?.label || PICK_MEDIA_ASK.label,
      hint: why?.hint || null,
      ...menuAnchor(pointer.current, why?.hint ? MENU_H + MENU_HINT_H : MENU_H),
    });
  }), []);

  /** The chosen source, uploaded, back to the well that asked. */
  const answerWith = useCallback(async (source) => {
    const max = pickAsk?.max || 1;
    const pending = ask.current;
    // Close first: the well is already showing "Uploading…", and the upload is
    // the slow part — leaving the pill up over it would read as a second ask.
    setPickAsk(null);
    let urls = [];
    try {
      if (pending?.payload.uploadTiming === 'on_submit') {
        let blob;
        if (source === 'sample') {
          const sample = sampleAddition(widgetId)?.payload;
          const url = sample?.image_url || sample?.photo_url || sample?.images?.[0];
          if (url) blob = await (await fetch(url, { signal: localMediaDraft.signal })).blob();
        } else {
          [blob] = await openFilePicker({ signal: localMediaDraft.signal });
        }
        if (blob && !localMediaDraft.signal.aborted) {
          const ref = localMediaDraft.stage(blob, pending.payload.previous_ref);
          if (ref) urls = [ref];
        }
      } else urls = await (source === 'sample' ? capture.uploadSample() : capture.uploadPickedFile(max));
    } catch { /* 取消或选图失败按空结果回送，保留替换前的照片。 */ }
    // 旧页面的 picker 不能覆盖新页面正在等待的答案。
    if (ask.current === pending) answerPick(urls);
  }, [answerPick, capture, pickAsk, localMediaDraft, widgetId]);

  // Escape dismisses, like any menu. (The backdrop covers the outside click.)
  useEffect(() => {
    if (!pickAsk) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') answerPick([]); };
    globalThis.addEventListener?.('keydown', onKey);
    return () => globalThis.removeEventListener?.('keydown', onKey);
  }, [pickAsk, answerPick]);

  // A pane that goes away with a question open still owes an answer.
  useEffect(() => () => { ask.current?.resolve([]); ask.current = null; }, [localMediaDraft]);

  // Mock mode renders an EXPLICIT feed only when the admin has injected a
  // lifecycle state or unselected the sample data; otherwise the runtime sources
  // its own clean base mock. (A '+' or a blueprint session button now opens the
  // REAL capture sheet in BOTH modes — it no longer lands a mock row here.)
  const feed = useMemo(() => {
    if (dataMode === 'user') return null;
    const hasStates = mockStates.size > 0;
    // "Mock Data" on with nothing else injected: return null so the runtime
    // sources the clean base mock itself — nothing pending, nothing deleting.
    if (showMock && !hasStates && !removedRows.size) return null;
    // "Mock Data" unselected → empty entries so the widget renders its empty
    // state (settings/pending shape kept so the page's config still resolves).
    let base = mockFeed(widgetId);
    if (!showMock) base = { ...base, entries: [] };
    if (hasStates) base = injectStates(base, mockStates, { widgetId, blueprint, pageId });
    // WHAT THE SHEET DELETED, taken out of the feed it was deleted from — rows
    // by their own id, an in-flight capture by every handle a pending cell is
    // keyed on, so dropping a session drops its placeholder AND its rows.
    if (removedRows.size) {
      const gone = (r) => rowHandles(r).some((h) => removedRows.has(h));
      const adds = (base.pending?.adds || []).filter((a) => !gone(a));
      base = {
        ...base,
        entries: (base.entries || []).filter((r) => !gone(r)),
        pending: { ...(base.pending || {}), adds, updates: base.pending?.updates || [] },
      };
    }
    return base;
  }, [dataMode, widgetId, mockStates, blueprint, pageId, showMock, removedRows]);

  /**
   * THE SHEET'S DELETE — the actual removal the long press never performs
   * itself. Two payload shapes, exactly as on the phone: a logged row
   * (`item_id`) and an in-flight capture (`grid_id`/`session_id`), whose delete
   * DROPS THE SESSION rather than a row.
   *
   * In "My data" mode a row delete is the real, host-owned one
   * (`DELETE /widget-instances/{id}/data/{entry_id}` — the same call the app's
   * `performWidgetRuntimeItemAction` makes) and the feed is re-read after it.
   * Everything else is the preview mutating its own preview: the studio owns no
   * session store, so a dropped capture disappears from this render and is
   * traced. The load-bearing delete is Flutter's.
   */
  const performDelete = useCallback(async (payload) => {
    const p = payload || {};
    const itemId = runtimeId(p.item_id ?? p.entry_id);
    const gridId = runtimeId(p.grid_id ?? p.session_id ?? p.photo_url ?? p.client_ref);
    const handle = itemId || gridId;
    setItemMenuBusy(true);
    try {
      if (itemId && dataMode === 'user' && capture.instanceId) {
        await widgetInstances.deleteData(capture.instanceId, itemId);
        setDeleteReload((k) => k + 1);
      }
      // Both handles when the cell carried both: a pending capture is keyed on
      // its client_ref in one lane and on its session in another.
      if (handle) {
        setRemovedRows((prev) => new Set([...prev, ...[itemId, gridId].filter(Boolean)]));
      }
      setTrace((prev) => [{
        op: 'crudRemove',
        payload: { ...p, ...(itemId ? { item_id: itemId } : { grid_id: gridId }) },
        at: Date.now(),
      }, ...prev].slice(0, 40));
    } catch (e) {
      setAddError(`Could not delete: ${e?.message || e}`);
    } finally {
      setItemMenuBusy(false);
      setItemMenu(null);
    }
  }, [capture.instanceId, dataMode]);

  /** The sheet's Save — the host's downloader, traced like every host slot. */
  const performSave = useCallback((url, payload) => {
    setTrace((prev) => [{ op: 'download', payload: { url, ...(payload || {}) }, at: Date.now() }, ...prev].slice(0, 40));
    try {
      const a = globalThis.document?.createElement?.('a');
      if (a) { a.href = url; a.download = ''; a.target = '_blank'; a.rel = 'noreferrer'; a.click(); }
    } catch { /* the host owns saving; a studio that cannot is not an error */ }
    setItemMenu(null);
  }, []);

  const stateChips = pageHasCollection(blueprint, pageId) ? MOCK_STATES : [];

  /**
   * ONE interaction store for the whole strip. The cards are top-down views of
   * the SAME running widget, so a filter tapped on the live preview has to show
   * applied in the focus card — with a store per card they drifted apart the
   * moment you touched anything. It lives ABOVE the page on purpose: the
   * applied filter already survived an index → data_view → index round-trip, so
   * the tile's own highlight must survive it too (it used to reset with the
   * component that a page change unmounted).
   */
  const interaction = useInteractionStore();

  /**
   * WHAT THE DIAGRAM ZONES READ — the preview's page, as it is NOW.
   *
   * The args the page was opened on, plus everything the preview has written to
   * `page_args` since (`liveArgs`). The preview itself keeps owning its own
   * copy: it is the app, and an app's page-local state is the page's, not the
   * studio's. This is the READING of it the other cards are handed, which is
   * why it flows one way only.
   */
  const livePageArgs = useMemo(
    () => (liveArgs.page === pageId && liveArgs.args ? { ...pageArgs, ...liveArgs.args } : pageArgs),
    [pageArgs, liveArgs, pageId],
  );

  /**
   * …and the same reading of the SLOT inbox: the shared store, with the last
   * pick answer re-addressed to the well it was picked for. Only the diagram
   * cards get it — the preview's own form correlated that answer itself and
   * would only settle the same well twice.
   */
  const mirroredInteraction = useMemo(() => {
    const m = pickMirror && pickMirror.page === pageId ? pickMirror : null;
    if (!m) return interaction;
    const forNode = interaction.slotState[m.nodeId] || {};
    return {
      ...interaction,
      slotState: {
        ...interaction.slotState,
        // A string seq can never collide with the reducer's counter, so a
        // mirrored delivery always reads as new — and never masks a real one
        // as "already seen".
        [m.nodeId]: { ...forNode, [m.slot]: { payload: m.payload, seq: `mirror:${m.seq}`, from: 'studio-mirror' } },
      },
    };
  }, [interaction, pickMirror, pageId]);

  /**
   * OPEN A CHAT for a session the widget continues (`openSession {session_id}`).
   * The app opens the runtime chat; the studio opens the same session page.
   */
  const openChat = useCallback((sessionId) => {
    if (!sessionId) return;
    try { window.open(`/playground/chat/${sessionId}`, '_blank', 'noopener'); } catch { /* jsdom */ }
  }, []);

  /**
   * TAKE THE PAGES NOW, DISPATCH LATER — the app's `_openDeferredCapture`.
   *
   * `open_capture {defer_dispatch: true, target_page: "edit.html"}` is a tile
   * saying "capture, but do NOT start a session yet". The phone obeys it in
   * three beats: run the camera, push the bundle page named by `target_page`
   * with the raw refs on `page_args.images`, and let THAT page's Confirm be
   * what creates the session — one document, one session, at Confirm and not at
   * the shutter. Without this branch the studio dispatched at the shutter and
   * the edit page was unreachable in the one place it is meant to be developed.
   *
   * Beat one is the popup: it is the studio's camera (see PICK_MEDIA_ASK /
   * DEFERRED_ASK), and its `[]` — dismissed, cancelled picker, failed upload —
   * is the phone user backing out. That answer must change NOTHING: no page
   * transition, no session, no half-opened editor over an empty stage.
   *
   * The page it lands on is opened with `{...args, images, prompt, title}`,
   * the phone's own page_args map: the tile's recipe has to survive the trip,
   * because the editor sits between the tile and the dispatch and the tile's
   * payload dies with the capture that opened the page.
   */
  const runDeferredCapture = useCallback(async (payload, page) => {
    const asked = here.current.pageId;
    const max = Number(sessionField(payload, 'max_images')) || DEFERRED_PAGES;
    const images = await askForMedia(max, DEFERRED_ASK);
    if (!images.length) return;
    // The preview stayed live through the upload; a capture belongs to the page
    // that asked for it, exactly as the capture sheet does.
    if (here.current.pageId !== asked) return;
    const extra = payload?.args && typeof payload.args === 'object' ? payload.args : null;
    const prompt = sessionPrompt(payload);
    const title = sessionField(payload, 'title');
    navigatePage(page.id, {
      ...extra,
      images,
      ...(prompt ? { prompt } : null),
      ...(title ? { title } : null),
    });
  }, [askForMedia, navigatePage]);

  /**
   * A SESSION SLOT FIRED IN THE LIVE PREVIEW — start it for real, the way the
   * app does. `openMainSession` dispatches the prompt to a new main session;
   * `openSession {session_id}` opens that session's chat; `openCapture` (and a
   * delegated `openPage` carrying a template's locked prompt) either opens the
   * real capture sheet SEEDED with the resolved payload, or — when the page has
   * ALREADY collected the photos — starts the session headlessly. Real in BOTH
   * data modes: the mode only chooses which feed the preview shows.
   *
   * `quick: true` WITH `images` is the app's "everything is collected, go"
   * (scene-studio's `Create scene`, whose wells hold the uploaded urls). The
   * phone opens no sheet for it, so neither does this: a sheet here would ask a
   * user who has just filled two wells for a photo, which is the bug the whole
   * 素材准备 page exists to avoid. Everything else — the host '+', a captureFab
   * with a prompt and no photo — still opens the sheet, because there the photo
   * is genuinely still missing.
   *
   * `closePage` reaches here only when it carries an ACTION — a page handing
   * back finished work on its way out, which is the far end of a deferred
   * capture. Everything else about `closePage` stays the bridge's own business.
   */
  const onSessionOpen = useCallback(async (slot, payload) => {
    setAddError(null);
    const prompt = sessionPrompt(payload);
    if (slot === 'openMainSession') { capture.dispatchMain(prompt); return; }
    if (slot === 'openSession') {
      const sid = sessionField(payload, 'session_id');
      if (sid) { openChat(String(sid)); return; }
      // No session bound on this page (index) — fall back to a seeded capture.
      capture.openSheetSeeded({ note: prompt });
      return;
    }
    // openCapture, or a delegated openPage whose preflight carries the prompt.
    const raw = sessionField(payload, 'images');
    const images = Array.isArray(raw) ? raw.filter((v) => typeof v === 'string' && v) : [];
    const deferred = images.some(LocalMediaDraft.isLocal);
    const sessionId = sessionField(payload, 'session_id');
    const quick = sessionField(payload, 'quick') === true;
    /**
     * `close_page {action: "capture", images}` — THE DISPATCH THE DEFERRED
     * CAPTURE OWED. The editor's Confirm files the pages it just baked and the
     * page closes in the same breath; the bridge is already taking the preview
     * back to the entry page, so all that is left here is the headless send —
     * the same one a `quick` capture makes, which is why the pending
     * placeholder, the banner, the SSE settle and the local landing all behave
     * identically. A close with an action but no pages files nothing: there is
     * no document to file, and an empty capture would only spin a placeholder.
     */
    if (slot === 'closePage') {
      // closePage 已结束草稿所有权；临时引用不能进入旧版编辑页结果派发通道。
      if (deferred) return;
      if (sessionField(payload, 'action') !== 'capture' || !images.length) return;
      capture.dispatchCapture({ note: prompt, images, sessionId });
      return;
    }
    // DEFER: this capture only takes the pages — see `runDeferredCapture`. Both
    // keys are additive hints, so anything that does not resolve (no
    // `target_page`, a page this bundle does not render, the page we are
    // already on) falls through to the ordinary capture below rather than
    // dropping it, exactly as the phone does.
    if (!deferred && sessionField(payload, 'defer_dispatch') === true) {
      const target = resolvePage(sessionField(payload, 'target_page'));
      if (target && target.id !== here.current.pageId) { runDeferredCapture(payload, target); return; }
    }
    const openSession = sessionField(payload, 'open_session') === true;
    // An explicit quick Session may start a text conversation without a photo.
    // Other no-media captures keep their existing interactive picker behavior.
    const textSession = openSession && quick && typeof prompt === 'string' && prompt.trim().length > 0;
    if ((quick || deferred) && (images.length || textSession)) {
      const asked = here.current.pageId;
      if (deferred && localMediaDraft.busy) return;
      if (openSession && sessionCaptureBusy.current) {
        // A repeated tap in the same visit waits for its original answer.
        // A different form must receive a rejection so its own loading state
        // clears and its preserved inputs remain retryable.
        if (sessionCaptureBusy.current !== captureOrigin) {
          setAddError('Another Session is still starting. Please try again in a moment.');
          host.triggerCaptureResult({ dispatched: false, reason: 'session_start_pending' });
        }
        return;
      }
      if (openSession) sessionCaptureBusy.current = captureOrigin;
      let dispatch;
      try {
        const prepared = deferred ? await localMediaDraft.prepare({
          ...payload, images,
          image_roles: sessionField(payload, 'image_roles'),
          required_media_count: sessionField(payload, 'required_media_count'),
        }) : payload;
        if (deferred && (!prepared || localMediaDraft.signal.aborted || here.current.pageId !== asked)) return;
        if (openSession && !captureOrigin.active) return;
        dispatch = await capture.dispatchCapture({
          note: prompt, images: deferred ? prepared.images : images, sessionId,
          ...(deferred ? { signal: localMediaDraft.signal } : {}),
        });
      } catch {
        if (here.current.pageId === asked && !localMediaDraft.signal.aborted && (!openSession || captureOrigin.active)) {
          host.triggerCaptureResult({ dispatched: false, reason: 'media_preparation' });
        }
        return;
      } finally {
        if (deferred) localMediaDraft.finish();
        if (openSession && sessionCaptureBusy.current === captureOrigin) sessionCaptureBusy.current = null;
      }
      // 只向发起请求的页面回送结果：成功后它会立即进统一结果页，
      // 失败时则用这个信号恢复 Create scene 并保留已选图片。
      if (dispatch && openSession && here.current.pageId === asked && captureOrigin.active) {
        leavingForSession.current = true;
      }
      if (here.current.pageId === asked && (!openSession || captureOrigin.active)) {
        host.triggerCaptureResult({
          dispatched: Boolean(dispatch),
          ...(!dispatch ? { reason: 'session_start_failed' } : null),
        });
      }
      // Session 失败时留在素材页；等待期间离开页面时也不再把用户拉回结果页。
      if (!dispatch || here.current.pageId !== asked || (openSession && !captureOrigin.active)) return;
      if (openSession) {
        // Navigate only after the existing upload/dispatch returned a real Session.
        // A same-tab route does not depend on popup permission after an async upload.
        navigate(`/playground/chat/${encodeURIComponent(dispatch.sessionId)}`);
        return;
      }
      const target = resolvePage(sessionField(payload, 'target_page'));
      if (target && target.id !== here.current.pageId) {
        const extra = payload?.args && typeof payload.args === 'object' ? payload.args : null;
        // 只有结果详情显式选择 Session 焦点时才注入定位；普通 gallery/edit 页保持旧参数。
        const targetArgs = sessionField(payload, 'target_focus') === 'source_session'
          ? {
            ...extra,
            session_id: dispatch.sessionId,
            source_session_id: dispatch.sessionId,
          }
          : (extra || {});
        navigatePage(target.id, targetArgs, {
          backToRoot: sessionField(payload, 'target_back') === 'widget_home',
        });
      }
      return;
    }
    capture.openSheetSeeded({
      note: prompt,
      images: images.length ? images : null,
      sessionId,
      quick,
      openSession: sessionField(payload, 'open_session') === true,
      sourceOrigin: captureOrigin,
    });
  }, [capture, captureOrigin, host, navigate, navigatePage, openChat, resolvePage, runDeferredCapture, localMediaDraft]);

  // Sync the opener and the picker into the refs the mock host calls (refs are
  // set in an effect, never during render).
  useEffect(() => {
    onSessionOpenRef.current = onSessionOpen;
    // The bridge calls this `(max, payload)`; the ask's second argument is the
    // REASON it is being asked, which only the studio decides. Wrapped so a
    // widget's own pick payload can never be read as one.
    pickSampleRef.current = (max, payload) => askForMedia(max, PICK_MEDIA_ASK, payload);
  });

  /**
   * Tapping '+' fires the widget's primary host slot. A session slot
   * (`openCapture` / `openMainSession`) starts a REAL session through
   * `onSessionOpen` (both data modes); anything else (a bare `logData`) just
   * traces, so the sheet is opened directly so the '+' still adds.
   */
  const addItem = useCallback(() => {
    setAddError(null);
    const slot = primary.slot;
    host[slot]?.({ action: 'add', widget_id: widgetId, source: 'host-fab' });
    if (!SESSION_TRIGGER.has(slot)) capture.openSheet();
  }, [capture, host, primary.slot, widgetId]);

  const adding = capture.busy;
  // The strip is the scroll container the focus wire measures against.
  const stripRef = useRef(null);
  const subview = capture.sheetOpen || pageId !== entryId;

  if (!blueprint) return null;

  const panel = {
    display: 'flex', flexDirection: 'column', gap: 14,
    background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16,
  };

  /**
   * THE VERSIONS TAB — the widget's real ladder, NATIVE rows only.
   *
   * A widget that grew a native blueprint usually still carries its HTML bundle
   * history, and this studio can neither preview nor sensibly pin those, so
   * they are filtered out of the list. Which makes one thing invisible that has
   * to stay visible: a PUBLIC pin sitting on a webview version means the app is
   * not being served the native widget at all. That is exactly what
   * `versionAttention` says, out loud, above the ladder and as a dot on the tab.
   */
  const versionsSection = (
    <section data-testid="versions-pane" style={panel}>
      {versionAttention ? (
        <div data-testid="versions-attention" style={{
          fontSize: 11.5, color: C.text, background: '#fff8e6',
          border: '1px solid #f0d9a0', borderRadius: 8, padding: '6px 10px', lineHeight: 1.5,
        }}>
          {publicEntry
            ? <>Public is pinned to <code>{publicEntry.version_label}</code>{publicEntry.view_type === 'ir' ? '' : ' (webview)'} — the latest native version is <code>{latestNative.version_label}</code>.</>
            : <>Nothing is pinned to Public — the latest native version is <code>{latestNative.version_label}</code>.</>}
        </div>
      ) : null}
      <VersionPane
        tags={tags}
        versionList={nativeList}
        versionsError={versionsError}
        selectedVersion={selectedVersion}
        onSelectVersion={(e) => setSelectedVersion(e.version_label)}
        emptyHint={`No widget row for “${widgetId}” in this environment. The blueprint is authored in-repo, so the preview still renders; publish once the widget exists here.`}
      />
      {/* The ladder is not empty, it just has no NATIVE row — say which, or the
          reader reads "no versions" and goes looking for lost uploads. */}
      {!versionsError && nativeVersions.length === 0 && allVersions.length > 0 ? (
        <div data-testid="no-native-version" style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>
          No native (<code>view_ir</code>) version published yet — this widget has {allVersions.length} webview
          version{allVersions.length === 1 ? '' : 's'}, which this studio does not list.
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={publish}
          disabled={!selectedVersion || notInEnv || busy === 'publish'}
          style={{
            ...btn(false), opacity: !selectedVersion || notInEnv ? 0.5 : 1,
            borderColor: C.good, color: C.good, fontWeight: 700,
            cursor: !selectedVersion || notInEnv ? 'not-allowed' : 'pointer',
          }}
        >{busy === 'publish' ? 'Publishing…' : 'Publish to Public'}</button>
        <button
          type="button"
          onClick={unpublish}
          disabled={!publicEntry || busy === 'clear'}
          style={{
            ...btn(false), opacity: publicEntry ? 1 : 0.5,
            borderColor: C.danger, color: C.danger, fontWeight: 700,
            cursor: publicEntry ? 'pointer' : 'not-allowed',
          }}
        >{busy === 'clear' ? 'Clearing…' : 'Delete Public'}</button>
        <button type="button" onClick={loadVersions} style={btn(false)}>Refresh</button>
      </div>

      {issues.length > 0 ? (
        <div style={{
          fontSize: 11.5, color: C.danger, background: '#fff1f0',
          border: '1px solid #ffd0cc', borderRadius: 8, padding: 10, lineHeight: 1.5,
        }}>
          <strong>Fail-closed report</strong>
          {issues.map((i, n) => (
            <div key={n}>unsupported {i.__unsupported}: <code>{i.name}</code>{i.selector ? ` (selector ${i.selector})` : ''}</div>
          ))}
        </div>
      ) : null}
    </section>
  );

  /** THE TRACE TAB — every `host.*` the running preview actually fired. */
  const traceSection = (
    <section data-testid="host-trace" style={panel}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 10, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Host trace
        </div>
        {navAction ? (
          <button
            type="button"
            data-testid="nav-action-trigger"
            onClick={() => host.triggerNavAction()}
            style={{ ...btn(false), alignSelf: 'flex-start' }}
          >{`Host nav action · ${navAction}`}</button>
        ) : null}
        {trace.length === 0 ? (
          <div style={{ fontSize: 11.5, color: C.textDim }}>
            Tap something in the preview — every `host.*` slot the blueprint fires lands here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {trace.map((t, i) => (
              <div key={`${t.at}-${i}`} style={{
                fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                background: '#f6f6f9', borderRadius: 6, padding: '4px 8px', color: C.text,
              }}>
                <strong>host.{t.op}</strong> {JSON.stringify(t.payload)}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="vi-native-detail">
      <DetailLayoutStyles />
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ ...btn(false), padding: '5px 10px' }}>‹ All widgets</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 14, height: 14, borderRadius: 7, background: blueprint.accent || '#999', border: '1px solid rgba(0,0,0,.1)' }} />
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{blueprint.title}</h2>
          <code style={{ fontSize: 11.5, color: C.textDim }}>{widgetId}</code>
          <span style={{ fontSize: 11, color: C.textDim }}>
            view_ir {blueprint.view_ir} · app ≥{blueprint.app_min_version}
          </span>
        </div>
        {/* the four readings, right-aligned on the widget's own header row */}
        <ViewTabs value={view} onChange={setView} attention={versionAttention} />
      </header>

      {/* a toolbar, then ONE horizontal strip: the live phone, and to its right
          whichever reading the header's tabs selected. In the `zone` reading
          that right side is more identical phone cards
          ([preview][data][focus][connection…]); otherwise it is one wide panel. */}
      <section style={{
        display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16,
      }}>
        {/* ROW 1 — page · appearance · data mode · and the mock-STATE chips.
            The chips inject lifecycle states into the current page's render
            on demand (see mockStates), so the base mock stays clean and you
            SELECT what you want to see rather than it being always-on. */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Segmented
            options={pages.map((p) => ({ value: p.id, label: p.id }))}
            value={pageId}
            onChange={(v) => {
              setPageId(v); setPageArgs({}); setBackStack([]);
              setFocusPage(v); setMockStates(new Set()); // states are page-scoped
            }}
          />
          <Segmented
            options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]}
            value={appearance}
            onChange={setAppearance}
          />
          <Segmented
            options={[{ value: 'mock', label: 'Mock data' }, { value: 'user', label: 'My data' }]}
            value={dataMode}
            onChange={setDataMode}
          />
          {dataMode === 'mock' ? (
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button" aria-pressed={showMock}
                onClick={() => setShowMock((v) => !v)}
                title="The widget's built-in sample data. Unselect to preview the empty state."
                style={{
                  padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `1px solid ${showMock ? C.accent : C.border}`,
                  background: showMock ? C.accent : C.bgCard, color: showMock ? '#fff' : C.text,
                }}
              >Mock Data</button>
              {stateChips.map((s) => {
                const on = mockStates.has(s.key);
                return (
                  <button
                    key={s.key} type="button" aria-pressed={on}
                    onClick={() => toggleMockState(s.key)}
                    title={`Inject the ${s.label.toLowerCase()} state into this page's mock render`}
                    style={{
                      padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                      border: `1px solid ${on ? C.accent : C.border}`,
                      background: on ? C.accent : C.bgCard, color: on ? '#fff' : C.text,
                    }}
                  >{s.label}</button>
                );
              })}
            </span>
          ) : null}
        </div>

        <div
          ref={stripRef}
          data-strip="1"
          data-testid="phone-strip"
          style={{
            position: 'relative', display: 'flex', gap: 16, alignItems: 'flex-start',
            // ONLY the cards scroll horizontally. overflow-y stays `hidden`, NOT
            // `visible`: CSS forces `visible` to compute as `auto` whenever the
            // other axis is `auto`, so `overflowY:'visible'` here silently added
            // a vertical scrollbar (the horizontal bar steals ~15px, tipping the
            // strip into vertical overflow). The cards size the strip, so nothing
            // real is clipped; paddingBottom clears the horizontal scrollbar.
            overflowX: 'auto', overflowY: 'hidden', paddingBottom: 16, maxWidth: '100%',
          }}
        >
          {/* CARD 1 — the live app. Fully interactive: real host actions, no
              inspect overlay. Picking a module to focus is the focus card's
              job, never this one's. */}
          <PhoneCard
            frameTestId="native-phone"
            frameRef={previewFrame}
            zone="Preview Zone"
            title={`${widgetId} · live`}
            sub={<>the real app — tap it, it works · page <code>{pageId}</code></>}
          >
            <NativeWidgetRuntime
              widgetId={widgetId}
              blueprint={blueprint}
              feed={feed}
              pageId={pageId}
              pageArgs={pageArgs}
              appearance={appearance}
              dataMode={dataMode}
              host={host}
              // the capture's own reload counter PLUS the sheet's: a delete
              // that really happened must re-read the feed too
              reloadKey={capture.reloadKey + deleteReload}
              pendingAdds={capture.pendingAdds}
              interaction={interaction}
              topInset={pageId !== entryId ? 48 : 0}
              headerLeading={!capture.sheetOpen ? <button type="button" aria-label="Back"
                onClick={pageId !== entryId ? goBack : onBack}
                style={{ width: 36, height: 36, borderRadius: 18, border: '1px solid #E3E3E3', background: 'white', color: '#1C1C1E', fontSize: 18, cursor: 'pointer' }}>←</button> : null}
              headerActions={[
                ...(navAction ? [{ label: navAction, onClick: () => host.triggerNavAction() }] : []),
                ...(blueprint.version_label ? [{ label: `ADMIN · ${blueprint.version_label}` }] : []),
              ]}
              previewVersion={selectedVersion}
              // marker-only — the ids a tap resolves through, nothing dimmed,
              // nothing boxed. See PREVIEW_INSPECT.
              inspect={PREVIEW_INSPECT}
              onReport={onReport}
              onTrace={onTrace}
              onDataState={setDataState}
            />
            {/* The host's back chevron — the WITHIN-widget back the app draws
                over every sub-page (the header's "‹ All widgets" LEAVES the
                widget). Its 48px is reserved via `topInset`, so it floats over
                the page's safe area instead of over its heading — the app's
                `_hostBackButtonHeight` contract. */}
            {pageId !== entryId && !capture.sheetOpen && !pageHeaderStyle(blueprint, pageId) ? (
              <button
                type="button"
                onClick={goBack}
                aria-label="Back"
                title="Host back — returns to the page you came from"
                style={{
                  position: 'absolute', left: 12, top: 8, width: 36, height: 36, zIndex: 3,
                  borderRadius: 18, border: 'none', padding: 0, fontSize: 19, lineHeight: '36px',
                  background: 'rgba(255,255,255,0.92)', color: '#1c1c1e',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.18)', cursor: 'pointer',
                }}
              >‹</button>
            ) : null}
            {/* The host's '+', drawn OVER the widget page — the blueprint never
                draws its own add button (registry `CaptureButton`, spec). Hidden in a
                sub-view, exactly like the app's bottom bar — and hidden entirely when
                the manifest sets `capture.show_fab:false` (a widget with its own '+'
                paths opts out of the global host FAB; mirrors the Flutter host gate
                `shouldShowWidgetRuntimeBottomBar`). */}
            {!subview && showFab ? (
              <button
                type="button"
                onClick={addItem}
                disabled={adding}
                aria-label="Add an item"
                title={`Host capture button → host.${primary.slot}`}
                style={{
                  position: 'absolute', right: 16, bottom: 22, width: 52, height: 52, zIndex: 3,
                  borderRadius: 26, border: 'none', padding: 0, color: '#fff', fontSize: 28,
                  lineHeight: '52px', fontWeight: 300,
                  background: blueprint.accent || C.accent,
                  boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
                  cursor: adding ? 'progress' : 'pointer', opacity: adding ? 0.6 : 1,
                }}
              >+</button>
            ) : null}
            {/* The REAL capture sheet — the same one the HTML widget runtime
                opens: pick a photo (upload → GCS), add a note, Send dispatches
                ONE session bound to this instance. Rendered in BOTH data modes:
                a '+' or a blueprint session button really starts a session
                regardless of which feed the preview displays. The
                `initialNote`/`initialImages`/`continueSessionId`/`quick` seed is
                what a blueprint `open_capture` (or a delegated template
                preflight) carried — the same fields the app's `_openCapture`
                feeds the sheet. A capture that already HOLDS its photos never
                reaches here at all: it dispatches headlessly, as on the phone. */}
            <WidgetCaptureOverlay
              open={capture.sheetOpen}
              onClose={capture.closeSheet}
              instanceId={capture.instanceId}
              widgetId={widgetId}
              widgetTitle={blueprint.title}
              initialNote={capture.seed?.note || undefined}
              initialImages={capture.seed?.images || undefined}
              continueSessionId={capture.seed?.sessionId || undefined}
              quick={capture.seed?.quick || undefined}
              onDispatchStarted={capture.dispatchStarted}
              onDispatchResolved={(clientRef, sessionId) => {
                const confirmed = typeof sessionId === 'string' ? sessionId.trim() : '';
                if (capture.seed?.openSession && !confirmed) {
                  capture.dispatchFailed(clientRef, new Error('The server did not return a Session ID'));
                  return;
                }
                capture.dispatchResolved(clientRef, sessionId);
                if (capture.seed?.openSession && capture.seed.sourceOrigin?.active && capture.seed.sourcePageId === here.current.pageId) {
                  leavingForSession.current = true;
                  navigate(`/playground/chat/${encodeURIComponent(confirmed)}`);
                }
              }}
              onDispatchFailed={capture.dispatchFailed}
            />
            {/* THE HOST'S LONG-PRESS SHEET — the phone's
                `showWidgetRuntimeItemOptions`, drawn over the page by the HOST
                and never by the widget. A long press (right-click / a 500ms
                hold) emits `removeRequested`, the blueprint wires it to
                `host.itemOptions`, and only this sheet's Delete removes
                anything. Save appears when the cell carried an `image_url` or
                `video_url`, exactly as on the phone. */}
            <ItemOptionsSheet
              payload={itemMenu}
              busy={itemMenuBusy}
              onDelete={performDelete}
              onSave={performSave}
              onCancel={() => { if (!itemMenuBusy) setItemMenu(null); }}
            />
          </PhoneCard>

          {/* CARDS 2..N — the `zone` reading, whose cards ARE strip items and
              so must stay direct siblings of the live phone. Every other
              reading is one wide panel in the same row. */}
          {view === 'zone' ? (
            <FocusExplorer
              blueprint={blueprint}
              widgetId={widgetId}
              appearance={appearance}
              // NO HOST, deliberately, and it is not a prop the explorer takes:
              // its cards are diagrams. The PREVIEW is the running app and the
              // only thing wired to the bridge.
              focus={focusNode}
              onFocus={setFocusNode}
              page={focusPage}
              registry={registry}
              dataMode={dataMode}
              instanceId={capture.instanceId}
              feed={feed}
              // the LIVE reading of the preview's page — its own writes
              // included — so the focus card is the same page in the same
              // state, never the page as it was opened an interaction ago
              interaction={mirroredInteraction}
              pageArgs={livePageArgs}
            />
          ) : (
            // ONE panel beside the preview. It takes the same zone band so its
            // top edge sits on the phone's baseline, flexes to fill the rest of
            // the row, and scrolls INSIDE itself — the strip clips its own
            // vertical overflow, so a long trace or ladder must not push past
            // the phone's height.
            <div
              data-testid="native-tab-region"
              data-view={view}
              style={{
                flex: '1 1 0', minWidth: 0, marginLeft: ZONE_GAP, paddingTop: ZONE_LABEL_H,
                maxHeight: SCREEN_H + ZONE_LABEL_H, overflowY: 'auto',
              }}
            >
              {view === 'module' ? <ModuleGraph blueprint={blueprint} /> : null}
              {view === 'trace' ? traceSection : null}
              {view === 'versions' ? versionsSection : null}
            </div>
          )}
        </div>

        {/* Notes below the strip: a banner that comes and goes must never move
            a card. */}
        {dataMode === 'user' && dataState !== 'ready' ? (
          <div style={{ fontSize: 11.5, color: C.textDim, background: '#f6f6f9', borderRadius: 8, padding: '6px 10px' }}>
            {dataState === 'not-adopted'
              ? 'You have not adopted this widget — the page renders its first-run state. Tap + to adopt it and log a row.'
              : dataState === 'empty'
                ? 'Your instance has no rows yet.'
                : String(dataState)}
          </div>
        ) : null}
        <div style={{ fontSize: 11.5, color: C.textDim, lineHeight: 1.5 }}>
          Host chrome: the <strong>+</strong> fires <code>host.{primary.slot}</code>
          {primary.from
            ? <> (wired from <code>{primary.from}</code>)</>
            : ' (no add affordance in the blueprint — the host owns the +)'}
          {' and opens the real capture sheet: photo → session → the agent logs back.'}
          {' Tapping a session button in the preview (a template, a “make another”) '}
          {'starts it for real too — in both data modes; one whose page already '}
          {'holds its photos dispatches with no sheet at all, exactly as on the phone. '}
          {'A capture that DEFERS its dispatch asks for the pages in the same pill the '}
          {'photo wells use, then opens its target page over them — nothing is sent '}
          {'until that page hands the work back.'}
        </div>
        {capture.session ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            fontSize: 11.5, color: C.text, background: '#f2f0ff',
            border: `1px solid ${C.accent}33`, borderRadius: 8, padding: '6px 10px',
          }}>
            <span>
              {capture.session.main ? 'Main session' : 'Session'} running{capture.session.id ? <> · <code>{capture.session.id.slice(0, 8)}</code></> : ' · starting…'}
              {capture.session.main ? '' : ' — the agent logs back into this widget.'}
            </span>
            {capture.session.id ? (
              <a
                href={`/playground/chat/${capture.session.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: C.accent, fontWeight: 600 }}
              >open ↗</a>
            ) : null}
            {/* The runner may not be reachable from a laptop — see the
                DEGRADATION note in useHostCapture. This lands the row now
                instead of waiting out the 30s timeout. A main session is not
                widget-bound, so there is nothing to land. */}
            {capture.session.main ? null : (
              <button type="button" onClick={capture.land} disabled={capture.busy} style={{ ...btn(false), marginLeft: 'auto' }}>
                {capture.busy ? 'Landing…' : 'Land it now'}
              </button>
            )}
          </div>
        ) : null}
        {/* A capture that could not reach a runner is NOT an error — the row
            still landed. Only a failed write is red. */}
        {capture.notice ? (
          <div style={{
            fontSize: 11.5, color: C.text, background: '#f6f6f9',
            border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', lineHeight: 1.5,
          }}>{capture.notice}</div>
        ) : null}
        {addError || capture.error ? (
          <div style={{
            fontSize: 11.5, color: C.danger, background: '#fff1f0',
            border: '1px solid #ffd0cc', borderRadius: 8, padding: '6px 10px',
          }}>{addError || capture.error}</div>
        ) : null}
      </section>

      {/* THE PICK MENU — the studio's CAMERA, floating at the point that was
          tapped: the answer to `host.pickMedia` AND the pages a deferred
          capture takes, one affordance for both. It is `position: fixed`, so it
          sits at the end of the pane rather than inside the phone frame (which
          clips and scrolls). PLAYGROUND-ONLY: the app has one picker and needs
          no such question. */}
      {pickAsk ? (
        <>
          {/* the outside click, as a real control — dismissing answers `[]`,
              and the backdrop is what stops that click also reaching the widget */}
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => answerPick([])}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              border: 'none', padding: 0, background: 'transparent', cursor: 'default',
            }}
          />
          <div
            data-testid="pick-media-menu"
            style={{
              position: 'fixed', left: pickAsk.x, top: pickAsk.y, zIndex: 201,
              width: MENU_W, display: 'flex', flexDirection: 'column', gap: 6, padding: 8,
              background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{
              fontSize: 10, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em',
              padding: '0 2px',
            }}>{pickAsk.label}</div>
            {pickAsk.hint ? (
              <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.45, padding: '0 2px' }}>
                {pickAsk.hint}
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => answerWith('sample')}
              title="This widget's representative photo, uploaded through the real upload path"
              style={{ ...btn(false), textAlign: 'left' }}
            >{pickAsk.deferred ? 'Choose Mock Image' : 'Upload Mock Image'}</button>
            <button
              type="button"
              onClick={() => answerWith('file')}
              title="Choose a file from this machine — the same upload, your own photo"
              style={{ ...btn(false), textAlign: 'left' }}
            >{pickAsk.deferred ? 'Choose My Image' : 'Upload My Image'}</button>
          </div>
        </>
      ) : null}
    </div>
  );
}
